import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from 'react-bootstrap';
import { PeopleFill } from 'react-bootstrap-icons';
import api from "../../services/api";
import { userMessageOf } from '../../services/apiError';
import { settleUsersEntry } from './userLoginsList.logic';
import styles from './UserLoginsManagement.module.scss';

// By UserRecordRealtimeState: 10 waiting for data, 20 receiving now; any other state is the offline dot.
const DOT_CLASS = {
  10: styles.waitingDataCircle,
  20: styles.liveDataCircle,
};

// One expanded row: the users and groups of a login, fetched into the page's cache (uid -> entry).
export default function UserLoginUsersPanel({ uid, entry, setUsersCache }) {
  const { t } = useTranslation();

  const load = async () => {
    const pending = { loading: true };
    const settle = next => setUsersCache(prev => settleUsersEntry(prev, uid, pending, next));

    setUsersCache(prev => new Map(prev).set(uid, pending));
    try {
      const response = await api.get(`/settings/admin/user-login/${uid}/users`);
      settle({ userList: response.data.userList });
    } catch (error) {
      settle({ error: true, message: userMessageOf(error) });
      console.error('Error loading the users of a login, status:', error?.response?.status);
    }
  };

  // Whenever the entry is missing, not only on the first expand: a Refresh empties the cache
  useEffect(() => {
    if (!entry) load();
  }, [uid, entry]);

  const renderUser = user => (
    <li key={user.key}>
      <span className={`${styles.dot} ${DOT_CLASS[user.onlineRecordState] ?? styles.dotOffline}`} aria-hidden="true" />
      {user.label}
    </li>
  );

  // One flat list from the server: direct users, then groups carrying their members in `nodes`
  const renderLists = userList => {
    const users = userList.filter(x => !x.isGroup);
    const groups = userList.filter(x => x.isGroup);
    if (users.length === 0 && groups.length === 0) {
      return <div className="text-muted">{t('admin.userLoginList.usersPanel.noUsers')}</div>;
    }

    return (
      <>
        {users.length > 0 && <ul className={styles.usersList}>{users.map(renderUser)}</ul>}
        {groups.map(g => (
          <div key={g.key}>
            <div className={styles.groupName}>
              <PeopleFill aria-hidden="true" />
              {g.label}
            </div>
            {g.nodes?.length > 0 && (
              <ul className={`${styles.usersList} ${styles.groupMembers}`}>{g.nodes.map(renderUser)}</ul>
            )}
          </div>
        ))}
      </>
    );
  };

  return (
    <div className={styles.usersPanel}>
      {(!entry || entry.loading) && (
        <div className="text-muted">
          <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
          {t('admin.userLoginList.usersPanel.loading')}
        </div>
      )}
      {entry?.error && (
        <Alert variant="danger" className="mb-0 py-2">
          {entry.message || t('admin.userLoginList.usersPanel.loadError')}{' '}
          <Button variant="link" className="alert-link p-0 border-0 align-baseline" onClick={load}>
            {t('admin.userLoginList.usersPanel.retry')}
          </Button>
        </Alert>
      )}
      {entry?.userList && renderLists(entry.userList)}
      <div>
        <Link to={`/admin/user-login/${uid}/settings/users`}>
          {t('admin.userLoginList.usersPanel.openPermissions')}
        </Link>
      </div>
    </div>
  );
}
